import { apiClient } from './client';
import type { ApiSuccess, PaginationMeta } from '../types';

export type BoqStatus = 'draft' | 'submitted' | 'approved' | 'rejected' | 'revised';
export type BoqItemType = 'material' | 'labour' | 'equipment' | 'other';

export interface EpcBoqItem {
  id: number;
  boqId: number;
  milestoneId: number | null;
  itemType: BoqItemType;
  description: string;
  unit: string | null;
  quantity: number;
  rate: number;
  amount: number;
  sequence: number;
  remarks: string | null;
}

export interface ParsedBoqRow {
  category?: string | null;
  sow?: string | null;
  description: string;
  unit?: string | null;
  quantity?: number | null;
  clientRate?: number | null;
  executionRate?: number | null;
  taxPercent?: number | null;
}
export interface ParseBoqResult {
  rows: ParsedBoqRow[];
  detectedColumns: Record<string, string>;
  message?: string;
}

export interface EpcBoq {
  id: number;
  boqCode: string;
  projectId: number;
  siteId: number | null;
  version: number;
  parentBoqId: number | null;
  title: string | null;
  description: string | null;
  status: BoqStatus;
  totalAmount: number;
  submittedAt: string | null;
  approvedAt: string | null;
  approvedBy: number | null;
  remarks: string | null;
  createdAt: string;
  // Enriched on the list endpoint.
  projectName?: string | null; projectCode?: string | null; clientName?: string | null;
  regionName?: string | null; stateName?: string | null;
  projectManagerName?: string | null; romName?: string | null;
  items?: EpcBoqItem[];
}

interface Paginated<T> { data: T[]; meta: PaginationMeta }

export interface BoqItemInput {
  milestoneId?: number | null;
  itemType?: BoqItemType;
  description: string;
  unit?: string | null;
  quantity: number;
  rate: number;
  sequence?: number;
  remarks?: string | null;
}

export const epcBoqApi = {
  async list(params: Record<string, unknown> = {}): Promise<Paginated<EpcBoq>> {
    const { data } = await apiClient.get<ApiSuccess<EpcBoq[]>>('/epc-boqs', { params });
    return { data: data.data, meta: data.meta as PaginationMeta };
  },
  get: async (id: number) => (await apiClient.get<ApiSuccess<EpcBoq>>(`/epc-boqs/${id}`)).data.data,
  create: async (body: { projectId: number; title?: string; description?: string }) =>
    (await apiClient.post<ApiSuccess<EpcBoq>>('/epc-boqs', body)).data.data,
  parse: async (fileUrl: string) => (await apiClient.post<ApiSuccess<ParseBoqResult>>('/epc-boqs/parse', { fileUrl })).data.data,
  async downloadTemplate() {
    const res = await apiClient.get('/epc-boqs/template', { responseType: 'blob' });
    const url = URL.createObjectURL(res.data as Blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'BOQ_Upload_Template.xlsx';
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  },
  uploadForSite: async (body: { siteId: number; title?: string; rows: ParsedBoqRow[] }) =>
    (await apiClient.post<ApiSuccess<EpcBoq>>('/epc-boqs/upload', body)).data.data,
  update: async (id: number, body: { title?: string | null; description?: string | null }) =>
    (await apiClient.put<ApiSuccess<EpcBoq>>(`/epc-boqs/${id}`, body)).data.data,
  remove: async (id: number) => (await apiClient.delete(`/epc-boqs/${id}`)).data,

  addItem: async (id: number, body: BoqItemInput) =>
    (await apiClient.post<ApiSuccess<EpcBoq>>(`/epc-boqs/${id}/items`, body)).data.data,
  updateItem: async (id: number, itemId: number, body: Partial<BoqItemInput>) =>
    (await apiClient.put<ApiSuccess<EpcBoq>>(`/epc-boqs/${id}/items/${itemId}`, body)).data.data,
  removeItem: async (id: number, itemId: number) =>
    (await apiClient.delete<ApiSuccess<EpcBoq>>(`/epc-boqs/${id}/items/${itemId}`)).data.data,

  submit: async (id: number) => (await apiClient.post<ApiSuccess<EpcBoq>>(`/epc-boqs/${id}/submit`, {})).data.data,
  approve: async (id: number, remarks?: string) => (await apiClient.post<ApiSuccess<EpcBoq>>(`/epc-boqs/${id}/approve`, { remarks })).data.data,
  reject: async (id: number, reason: string) => (await apiClient.post<ApiSuccess<EpcBoq>>(`/epc-boqs/${id}/reject`, { reason })).data.data,
  revise: async (id: number, remarks?: string) => (await apiClient.post<ApiSuccess<EpcBoq>>(`/epc-boqs/${id}/revise`, { remarks })).data.data,
};
